/* eslint-disable react-hooks/exhaustive-deps */
import { motion, AnimatePresence } from 'framer-motion';
import { useEffect, useState, createRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePostMessage } from 'spectacle-ui-game-bridge-react';
import classNames from 'classnames';
import { PreloginContent } from '../../hooks/useContentForBuild';
import { usePreloginContext } from '../../context/PreloginContext';
import Loader from '../../components/loader/loader';
import Modal from '../modal/modal';
import PasswordIcon from '../../components/passwordIcon/passwordIcon';
import useBackButton from '../../hooks/useBackButton';
import useDlogLoginFunnel from '../../hooks/useDlogLoginFunnel';
import useFocusInputs from '../../hooks/useFocusInputs';
import './signIn.sass';

type SignInComponent = {
  content: PreloginContent | null;
  isShown: boolean;
  hide: () => void;
  handleUrl: (e: any) => void;
};

type Error = {
  message: string | undefined;
};

export default function SignIn({ content, isShown, hide, handleUrl }: SignInComponent) {
  const navigate = useNavigate();
  const { accountRegistrationStatus } = usePreloginContext();
  const [isPasswordShown, setIsPasswordShown] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const closeWebView = usePostMessage('closeWebView');
  const passPreloginFence = usePostMessage('passPreloginFence');
  const gamelogin = usePostMessage('login');
  const [isShowingLoader, setIsShowingLoading] = useState<boolean>(false);
  const formElement = createRef<HTMLFormElement>();
  const [setOffset, offsetStyle, onFocus, onBlur] = useFocusInputs();
  const dlog = useDlogLoginFunnel();

  useBackButton(isShown, hide);

  const passwordInputRef = useCallback((node: any) => {
    if (node !== null) {
      setOffset('password', node.getBoundingClientRect().top);
    }
  }, []);

  const emailInputRef = useCallback((node: any) => {
    if (node !== null) {
      setOffset('email', node.getBoundingClientRect().top);
    }
  }, []);

  const [form, setForm] = useState({
    email: '',
    password: '',
  });

  const togglePassword = (e: any) => {
    e.preventDefault();
    setIsPasswordShown(!isPasswordShown);
  };

  const login = () => {
    onBlur();

    dlog({
      phase: 'Login_Popup',
      action: 'Click',
    });

    setIsShowingLoading(true);
    gamelogin(
      JSON.stringify({
        email: form.email,
        password: form.password,
      })
    );
  };

  const handleLogin = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsPasswordShown(false);
    validateForm(() => login());
  };

  const handleFormChange = (e: React.FormEvent<any>) => {
    const target = e.target as any;
    const updateTarget = target.name;

    setForm((prev: any) => ({
      ...prev,
      [updateTarget]: target.value,
    }));
  };

  const handleKeyDown = (e: React.KeyboardEvent<any>) => {
    if (!formElement.current) {
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      const target = e.target as HTMLInputElement;
      const next: HTMLElement | null = formElement.current.querySelector(`[tabIndex="${Number(target.tabIndex) + 1}"]`);

      if (next) {
        next.focus();
      } else {
        target.blur();
        validateForm(() => login());
      }
    }
  };

  const validateForm = (action: Function) => {
    if (formElement.current) {
      const _form = formElement.current;

      if (_form.checkValidity()) {
        setError(null);
        action();
      } else {
        const emailValidity = _form.email.validity;

        if (emailValidity.typeMismatch || emailValidity.valueMissing || _form.password.validity.valueMissing) {
          setError({
            message: content?.signInError,
          });
        }
      }
    }
  };

  useEffect(() => {
    if (isShown) {
      if (accountRegistrationStatus === 'AccountRegistrationSuccess') {
        dlog({
          phase: 'Login_Popup',
          action: 'Success',
        });

        passPreloginFence();

        if (content?.enablePostLoginMessages) {
          navigate('/post-login');
        } else {
          closeWebView();
        }
      }

      if (accountRegistrationStatus === 'AccountRegistrationFailure') {
        dlog({
          phase: 'Login_Popup',
          action: 'Error',
        });

        setError({
          message: content?.signInError,
        });
      }

      if (accountRegistrationStatus === 'AccountRegistrationMultiLogin') {
        dlog({
          phase: 'Login_Popup',
          action: 'Error',
        });

        setError({
          message: content?.errorMultipleLogins,
        });
      }

      setIsShowingLoading(false);
    }
  }, [accountRegistrationStatus, passPreloginFence, closeWebView, isShown, content]);

  useEffect(() => {
    if (isShown) {
      dlog({
        phase: 'Login_Popup',
        action: 'Render',
      });
    }
  }, [isShown, dlog]);

  return (
    <>
      <Loader isShown={isShowingLoader} />
      <AnimatePresence>
        {isShown && content && (
          <motion.div key="modal" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="signin">
            <Modal style={offsetStyle} toggle={hide}>
              <form ref={formElement} onSubmit={handleLogin} noValidate autoComplete="off">
                <div id="signin__form">
                  <div className="signin__logo">
                    <img src="https://prod.cdni.callofduty.com/prelogin/media/menu_cod_logo_lg.png" alt="Call of Duty" />
                  </div>
                  <h3>{content.accountLogin}</h3>
                  {error && <div className="signin__error">{error?.message}</div>}
                  <label>
                    {content.signInEmailLabel}
                    <span>*</span>
                  </label>
                  <input enterKeyHint="next" tabIndex={1} required name="email" type="email" onChange={handleFormChange} onKeyDown={handleKeyDown} placeholder={content.signInEmailPlaceholder} onFocus={onFocus} onBlur={onBlur} ref={emailInputRef} disabled={isShowingLoader} />
                  <div className="signin__form__password">
                    <label>
                      {content.signInPasswordLabel}
                      <span>*</span>
                    </label>
                    <div className="signin__form__password-input">
                      <input enterKeyHint="done" tabIndex={2} required name="password" type={isPasswordShown ? 'text' : 'password'} onKeyDown={handleKeyDown} onChange={handleFormChange} placeholder={content.signInPasswordPlaceholder} onInput={(e) => (e.target as HTMLInputElement).setCustomValidity('')} onFocus={onFocus} onBlur={onBlur} ref={passwordInputRef} autoComplete="off" disabled={isShowingLoader} />
                      <button className={classNames('signin__form__password-icon', isPasswordShown ? 'signin__form__password-icon--show' : '')} onClick={togglePassword} type="button" disabled={isShowingLoader}>
                        <PasswordIcon />
                      </button>
                    </div>
                  </div>
                  <a onClick={handleUrl} href={content.urlForgotPassword}>
                    {content.signInForgotPasswordLink}
                  </a>
                  <button className="cta cta--full" type="submit" disabled={isShowingLoader}>
                    {content.signInLoginCta}
                  </button>
                </div>
              </form>
            </Modal>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
